import { useState } from "react";
import { useTranslation } from "react-i18next";
import ModalCloseButton from "@/components/base/ModalCloseButton";
import {
  MODAL_FOOTER_CANCEL_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_STYLE,
} from "@/components/base/modalFooterButtonStyles";
import { getStudyErrorMessage, type Study, type StudySaveInput } from "@/services/studies";

interface NewStudyModalProps {
  initialStudy?: Study | null;
  onClose: () => void;
  onSave: (study: StudySaveInput) => Promise<void>;
}

export default function NewStudyModal({ initialStudy, onClose, onSave }: NewStudyModalProps) {
  const { t } = useTranslation();
  const isEdit = !!initialStudy;
  const [name, setName] = useState(initialStudy?.name ?? "");
  const [code, setCode] = useState(initialStudy?.code ?? "");
  const [nameTouched, setNameTouched] = useState(false);
  const [codeTouched, setCodeTouched] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const nameRequired = !name.trim();
  const codeRequired = !code.trim();
  const nameError = nameTouched && nameRequired ? t("common.required") : null;
  const displayedCodeError = codeTouched && codeRequired ? t("common.required") : codeError;
  const canSave = !nameRequired && !codeRequired && !submitting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;

    setSubmitting(true);
    setCodeError(null);
    setFormError(null);
    try {
      await onSave({
        name: name.trim(),
        code: code.trim(),
      });
    } catch (error) {
      const message = getStudyErrorMessage(error, t("studies.modal.saveFailed"));
      if (message === "This study code already exists") {
        setCodeError(message);
      } else {
        setFormError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
    >
      <div
        className="bg-white rounded-xl w-[480px] max-w-[90vw] overflow-hidden"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.14)" }}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            {isEdit ? t("studies.modal.editTitle") : t("studies.modal.title")}
          </h3>
          <ModalCloseButton ariaLabel={t("common.close")} onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit}>
          {/* Body */}
          <div className="px-5 py-5 flex flex-col gap-4">
            {formError && (
              <div className="flex items-start gap-2 px-3 py-2 text-xs text-red-700 border border-red-200 bg-red-50 rounded-lg">
                <i className="ri-error-warning-line text-sm mt-px"></i>
                <span>{formError}</span>
              </div>
            )}

            {/* Study Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {t("studies.modal.studyName")}{" "}
                <span className="text-red-400">{t("studies.modal.studyNameRequired")}</span>
              </label>
              <input
                type="text"
                value={name}
                maxLength={120}
                onChange={(event) => {
                  setName(event.target.value);
                  setFormError(null);
                }}
                onBlur={() => setNameTouched(true)}
                placeholder={t("studies.modal.studyNamePlaceholder")}
                aria-required="true"
                aria-invalid={!!nameError}
                aria-describedby={nameError ? "study-name-error" : undefined}
                className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors ${
                  nameError
                    ? "border-red-300 focus:border-red-400"
                    : "border-gray-200 focus:border-gray-300"
                }`}
              />
              {nameError && (
                <p id="study-name-error" role="alert" className="text-[11px] text-red-500 mt-1">
                  {nameError}
                </p>
              )}
            </div>

            {/* Study Code */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {t("studies.modal.studyCode")}{" "}
                <span className="text-red-400">{t("studies.modal.studyCodeRequired")}</span>
              </label>
              <input
                type="text"
                value={code}
                maxLength={120}
                onChange={(event) => {
                  setCode(event.target.value);
                  setCodeError(null);
                  setFormError(null);
                }}
                onBlur={() => setCodeTouched(true)}
                placeholder={t("studies.modal.studyCodePlaceholder")}
                aria-required="true"
                aria-invalid={!!displayedCodeError}
                aria-describedby={displayedCodeError ? "study-code-error" : undefined}
                className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors ${
                  displayedCodeError
                    ? "border-red-300 focus:border-red-400"
                    : "border-gray-200 focus:border-gray-300"
                }`}
              />
              {displayedCodeError && (
                <p id="study-code-error" role="alert" className="text-[11px] text-red-500 mt-1">
                  {displayedCodeError}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className={MODAL_FOOTER_CANCEL_BUTTON_CLASS}>
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={!canSave}
              className={`${MODAL_FOOTER_PRIMARY_BUTTON_CLASS} disabled:opacity-40 disabled:cursor-not-allowed`}
              style={MODAL_FOOTER_PRIMARY_BUTTON_STYLE}
            >
              {submitting ? (
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin"></i>
                  {t("common.saving")}
                </span>
              ) : (
                t("common.save")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
